import React from "react";
import { Mail, MessageCircle, Eye, FileText, AlertTriangle, Paperclip } from "lucide-react";
import { previewDoc } from "../lib/docSend";

const inputCls = "w-full px-2.5 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";

function Channel({ id, icon: Icon, label, form, setForm, testid }) {
  return (
    <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs cursor-pointer transition-colors ${form.channels[id] ? "border-norea-primary bg-norea-primary/10 text-norea-primary" : "border-norea-border text-norea-textMuted"}`}>
      <input type="checkbox" className="sr-only" checked={form.channels[id]} onChange={(e) => setForm({ ...form, channels: { ...form.channels, [id]: e.target.checked } })} data-testid={testid} />
      <Icon className="w-3.5 h-3.5" /> {label}
    </label>
  );
}

export function MissingVars({ missing, testid }) {
  if (!missing?.length) return null;
  return (
    <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid={testid}>
      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>Variables vides sur ce dossier : <strong>{missing.map((m) => `${m.label}${m.group ? ` (${m.group})` : ""}`).join(", ")}</strong>. Elles apparaissent en « [à compléter : …] » : corrigez le texte ou complétez le dossier puis rechargez le modèle.</span>
    </div>
  );
}

function Attachments({ form, setForm, prefix }) {
  if (!form.attachments?.length) return null;
  const toggle = (i) => setForm({ ...form, attachments: form.attachments.map((a, k) => (k === i ? { ...a, on: !a.on } : a)) });
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-attachments`}>
      <span className="text-[11px] text-norea-textMuted inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> Pièces jointes du modèle :</span>
      {form.attachments.map((a, i) => (
        <button key={a.key} type="button" disabled={!a.files} onClick={() => toggle(i)} title={a.files ? "" : "Pas encore déposée dans l'onglet Dossier"}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${!a.files ? "border-dashed border-amber-300 text-amber-700 cursor-not-allowed" : a.on ? "border-norea-primary bg-norea-primary/10 text-norea-primary" : "border-norea-border text-norea-textMuted line-through"}`}
          data-testid={`${prefix}-attachment-${a.id}`}>
          {a.label}{!a.files && " — absente"}
        </button>
      ))}
    </div>
  );
}

export default function SendForm({ dossierId, form, setForm, onRole, onTemplate, prefix = "send", lockRole = false }) {
  const set = (part, k) => (e) => setForm({ ...form, [part]: { ...form[part], [k]: e.target.value } });
  const rec = form.recipients.find((r) => r.role === form.role);
  return (
    <div className="space-y-3" data-testid={`${prefix}-form`}>
      {onTemplate && form.templates?.length > 0 && (
        <label className="block text-xs text-norea-textMuted space-y-1"><span>Modèle de message</span>
          <select value={form.template_id || ""} onChange={(e) => onTemplate(e.target.value)} className={inputCls} data-testid={`${prefix}-template`}>
            {form.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      )}
      <MissingVars missing={form.missing} testid={`${prefix}-missing-vars`} />
      <Attachments form={form} setForm={setForm} prefix={prefix} />
      <div className="flex flex-wrap gap-2">
        {form.docs.map((d) => (
          <button key={d.id} onClick={() => previewDoc(dossierId, d)} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-norea-border hover:border-norea-primary hover:text-norea-primary transition-colors" data-testid={`${prefix}-preview-${d.id}`}>
            <FileText className="w-3.5 h-3.5" /> {d.name} <Eye className="w-3 h-3" />
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs text-norea-textMuted space-y-1"><span>Destinataire</span>
          <select value={form.role} disabled={lockRole} onChange={(e) => onRole(e.target.value)} className={inputCls} data-testid={`${prefix}-role`}>
            {form.recipients.map((r) => <option key={r.role} value={r.role}>{`${r.label} — ${r.name}`}</option>)}
            {!rec && <option value={form.role}>{form.role}</option>}
          </select>
        </label>
        <div className="text-xs text-norea-textMuted space-y-1"><span>Canal</span>
          <div className="flex gap-2">
            <Channel id="email" icon={Mail} label="Email" form={form} setForm={setForm} testid={`${prefix}-channel-email`} />
            <Channel id="whatsapp" icon={MessageCircle} label="WhatsApp" form={form} setForm={setForm} testid={`${prefix}-channel-whatsapp`} />
          </div>
        </div>
      </div>
      {rec && !rec.emails?.length && !rec.whatsapp && (
        <div className="text-[11px] text-amber-700" data-testid={`${prefix}-no-contact`}>Aucun email ni WhatsApp dans l'annuaire pour {rec.name} : saisissez-les ci-dessous (ou complétez la fiche dans Configuration → Annuaire).</div>
      )}
      {form.channels.email && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 rounded border border-norea-border bg-norea-bg/40" data-testid={`${prefix}-email-block`}>
          <label className="text-xs text-norea-textMuted space-y-1"><span>À</span><input value={form.email.to} onChange={set("email", "to")} className={inputCls} placeholder="email@exemple.fr" data-testid={`${prefix}-email-to`} /></label>
          <label className="text-xs text-norea-textMuted space-y-1"><span>Copies (séparées par des virgules)</span><input value={form.email.cc} onChange={set("email", "cc")} className={inputCls} data-testid={`${prefix}-email-cc`} /></label>
          <label className="md:col-span-2 text-xs text-norea-textMuted space-y-1"><span>Objet</span><input value={form.email.subject} onChange={set("email", "subject")} className={inputCls} data-testid={`${prefix}-email-subject`} /></label>
          <label className="md:col-span-2 text-xs text-norea-textMuted space-y-1"><span>Message{form.docs.length ? " (PDF en pièce jointe)" : ""}</span><textarea rows={6} value={form.email.body} onChange={set("email", "body")} className={inputCls} data-testid={`${prefix}-email-body`} /></label>
        </div>
      )}
      {form.channels.whatsapp && (
        <div className="grid grid-cols-1 gap-2 p-3 rounded border border-emerald-200 bg-emerald-50/40" data-testid={`${prefix}-whatsapp-block`}>
          <label className="text-xs text-norea-textMuted space-y-1 max-w-xs"><span>Numéro WhatsApp</span><input value={form.whatsapp.to} onChange={set("whatsapp", "to")} className={inputCls} placeholder="33612345678" data-testid={`${prefix}-wa-to`} /></label>
          <label className="text-xs text-norea-textMuted space-y-1"><span>Message — « {"{liens}"} » sera remplacé par les liens sécurisés vers les PDF (valables 30 jours)</span><textarea rows={5} value={form.whatsapp.text} onChange={set("whatsapp", "text")} className={inputCls} data-testid={`${prefix}-wa-text`} /></label>
        </div>
      )}
    </div>
  );
}
